import type { Environment } from '@/contexts/environment';

// SERVER-ONLY environment vars, secrets, and the assembled `Environment` config.
//
// This module must NEVER be imported from a client (`'use client'`) module. It is
// the single home for every `process.env` read in the app. It is edge-safe (no
// `next/headers`), so middleware may import from it. Client-facing values are part
// of the `Environment` config assembled below and reach the browser through
// `getEnvironment()` -> `EnvironmentProvider` -> `useEnvironment()`, never by a
// direct client-side `process.env` read.
//
// (`server-only` is not a dependency in this repo, so the boundary is enforced by
// convention + review rather than a build-time guard.)
//
// Vars are grouped by resolution regime:
//   - BUILD-TIME: listed in `next.config.mjs` `env:`, so the bundler string-
//     replaces it at `next build`; changing it requires a rebuild.
//   - RUNTIME: read from `process.env` when the server executes; can vary per
//     deployment of the same build.

// ===== BUILD-TIME (next.config.mjs `env:`) =====

/** Internal API Gateway base URL for server-to-server fetches. */
export const apiGatewayUrl = process.env.API_GATEWAY_URL;

// ===== RUNTIME =====

/** Disables authentication entirely. Demo builds only, paired with `isDemo`. */
export const disableAuth = process.env.DISABLE_AUTH === 'true';

/** Sentry DSN, used server-side and in the layout's inline client bootstrap. */
export const sentryDsn = process.env.SENTRY_DSN;

/** Deploy environment name ('prod' | 'testing' | ...). Raw; callers lowercase/truncate. */
export const environmentName = process.env.ENVIRONMENT ?? '';

/** Next.js server runtime ('nodejs' | 'edge'); gates instrumentation registration. */
export const nextRuntime = process.env.NEXT_RUNTIME;

/** Google OAuth client id (NextAuth provider). */
export const googleClientId = process.env.GOOGLE_CLIENT_ID;
/** Google OAuth client secret (NextAuth provider). Secret. */
export const googleClientSecret = process.env.GOOGLE_CLIENT_SECRET;

/** HMAC secret for signing the Fern docs JWT. Secret. */
export const fernJwtSecret = process.env.FERN_JWT_SECRET;

// Client-safe `Environment` fields (mostly RUNTIME; the two console vars are
// BUILD-TIME, noted below). Kept module-private and exposed only through
// `getEnvironment()` — callers must never read these directly. The assembled
// object is serialized to the browser by `EnvironmentProvider` (via
// getEnvironment), so it must contain no secrets.

/** Demo build: drives demo-only UI and disables real auth. */
const isDemo = process.env.IS_DEMO === 'true';
/** Local `next dev`. */
const isDev = process.env.NODE_ENV === 'development';
/** Test build: Next test mode or the explicit IS_TESTING flag (Playwright). */
const isTest =
  process.env.NODE_ENV === 'test' || process.env.IS_TESTING === 'true';
/** Opens the mock console's org-scope session switch. Off unless a deployment opts in. */
const enableOrgDemo = process.env.ENABLE_ORG_DEMO === 'true';
/** Force a client logout when an API call returns 401. */
const enableForceLogoutOn401 =
  process.env.ENABLE_FORCE_LOGOUT_ON_401 === 'true';
/** Base URL the browser hits for API calls; `/api/mock` selects the mock routes. */
const clientApiBaseUrl = process.env.CLIENT_API_BASE_URL ?? '';
/** Eon service-account id, surfaced in onboarding instructions. */
const serviceAccountId = process.env.SERVICE_ACCOUNT_ID || '';
/** GCP control-plane service account, surfaced in GCP onboarding. */
const gcpControlPlane = process.env.GCP_CONTROL_PLANE || '';
/** Git SHA of the running build (footer / support diagnostics). BUILD-TIME (next.config `env:`, the build TAG). */
const gitCommit = process.env.TAG || '';
/** LaunchDarkly client-side SDK key. */
const launchDarklyClientId = process.env.LAUNCH_DARKLY_CLIENT_ID;
/** PostHog client-side project key (analytics). */
const posthogKey = process.env.POSTHOG_KEY;
/** Base/CDN prefix for build-hosted static assets (Monaco `vs/`). BUILD-TIME (next.config `env:`). Empty => relative. */
const consoleAssetPrefix = process.env.CONSOLE_ASSET_PREFIX ?? '';
/** Base URL for release-hosted MFE remotes (e.g. lakehouse). BUILD-TIME (next.config `env:`). */
const consoleReleasesBaseUrl = process.env.CONSOLE_RELEASES_BASE_URL ?? '';

const environment: Environment = {
  isDemo,
  isDev,
  isTest,
  enableOrgDemo,
  enableForceLogoutOn401,
  clientApiBaseUrl,
  serviceAccountId,
  gcpControlPlane,
  gitCommit,
  launchDarklyClientId,
  posthogKey,
  consoleAssetPrefix,
  consoleReleasesBaseUrl,
};

/** The single accessor for the client-safe environment config. */
export const getEnvironment = (): Environment => environment;

// Secure cookies everywhere except local dev, test builds, and test environments.
export const useSecureCookies = !(
  isDev ||
  isTest ||
  environmentName.toLowerCase() === 'testing'
);
